import React, { useState } from "react";
import { BookmarkCheck, Loader2 } from "lucide-react";
import { message } from "antd";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import { createSavedSearchResult } from "Explore/Services/SavedSearchResults";

const SaveResultsModal = ({
  open,
  onOpenChange,
  entityType = "researchers",
  currentItems = [],
  currentFilters = {},
  defaultName = "",
  onSavedSuccess,
  fetchItems,
}) => {
  const [name, setName] = useState(defaultName || "");
  const [limit, setLimit] = useState("20");
  const [saving, setSaving] = useState(false);

  // Sync default name if opened
  React.useEffect(() => {
    if (open) {
      const dateStr = new Date().toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const entityLabel =
        entityType === "units"
          ? "Units"
          : entityType === "institutions"
          ? "Institutions"
          : "Researchers";
      setName(defaultName || `${entityLabel} Search - ${dateStr}`);
    }
  }, [open, defaultName, entityType]);

  const handleSave = async () => {
    if (!name.trim()) {
      message.warning("Please provide a name for this collection.");
      return;
    }

    setSaving(true);
    try {
      const savedLimit = Number(limit) || 20;
      let itemsToSave = [];

      // 1. Ưu tiên fetch đủ snapshot nếu có hàm fetchItems được truyền vào
      if (typeof fetchItems === "function") {
        const fetched = await fetchItems(savedLimit);
        itemsToSave = Array.isArray(fetched) ? fetched : fetched?.results || [];
      } else {
        itemsToSave = currentItems.slice(0, savedLimit);
      }

      // 2. Fallback nếu kết quả fetch rỗng hoặc cắt vừa đúng limit
      if (!itemsToSave || itemsToSave.length === 0) {
        itemsToSave = currentItems.slice(0, savedLimit);
      } else if (itemsToSave.length > savedLimit) {
        itemsToSave = itemsToSave.slice(0, savedLimit);
      }

      if (itemsToSave.length === 0) {
        message.warning("No items found to save.");
        setSaving(false);
        return;
      }

      // 3. Gửi snapshot lên Backend
      const created = await createSavedSearchResult({
        name: name.trim(),
        entityType,
        filters: currentFilters,
        limit: savedLimit,
        items: itemsToSave,
      });

      message.success(`Saved top ${itemsToSave.length} results to My Library!`);
      if (onSavedSuccess) {
        onSavedSuccess(created);
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to save search results", err);
      let errorMsg = "Failed to save search results.";
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        errorMsg = detail[0].msg;
      } else if (typeof detail === "string") {
        errorMsg = detail;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white shadow-xl border-gray-200">
        <DialogHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-lg bg-explore/10 text-explore flex items-center justify-center border border-explore/20">
              <BookmarkCheck size={20} />
            </div>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Save Search Results
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-gray-500">
            Save this snapshot of search results to your library to view later as list or network graph and export anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-3">
          {/* Collection Name */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Collection Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AI Scholars Stanford 2026"
              className="h-9 text-sm focus-visible:ring-explore"
              autoFocus
            />
          </div>

          {/* Top N selection */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Save Top Results
            </label>
            <Select modal={false} value={limit} onValueChange={setLimit}>
              <SelectTrigger className="h-9 bg-white text-sm">
                <SelectValue placeholder="Select limit" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-xl border-gray-200 z-[99999]">
                <SelectItem value="20" className="text-xs cursor-pointer">Top 20 results</SelectItem>
                <SelectItem value="100" className="text-xs cursor-pointer">Top 100 results</SelectItem>
                <SelectItem value="500" className="text-xs cursor-pointer">Top 500 results</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400">
              {fetchItems
                ? `Will fetch and save up to ${limit} top results matching current filters.`
                : `Currently available in this session: ${currentItems.length} items.`}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="bg-explore hover:bg-explore/90 text-white shadow-xs"
          >
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
            {saving ? "Saving..." : "Save to Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveResultsModal;
